// src/store/notificationStore.ts
import { create } from "zustand";
import { db } from "@/lib/firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  arrayRemove,
} from "firebase/firestore";
import { useAuthStore } from "@/store/authStore";

interface NotificationState {
  unreadTasksCount: number;
  unreadMessagesCount: number;
  unreadDocumentsCount: number;

  init: () => void;
  cleanup: () => void;
  incrementDocumentNotifications: (count?: number) => void;

  markTaskAsRead: (taskId: string) => Promise<void>;
  markDocumentAsRead: (docId: string) => Promise<void>;

  resetCounters: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => {
  let unsubTasks: (() => void) | null = null;
  let unsubTaskMessages: (() => void) | null = null;
  let unsubDocuments: (() => void) | null = null;

  return {
    unreadTasksCount: 0,
    unreadMessagesCount: 0,
    unreadDocumentsCount: 0,

    init: () => {
      const { user } = useAuthStore.getState();
      if (!user) return;

      const userId = String(user.id || user.uid || "");
      console.log("[notificationStore] Инициализация для пользователя:", userId);

      // Задачи
      const tasksQuery = query(
        collection(db, "tasks"),
        where("unreadBy", "array-contains", userId)
      );
      unsubTasks = onSnapshot(tasksQuery, (snap) => {
        set({ unreadTasksCount: snap.size });
      });

      // Сообщения в задачах
      const allTasksQuery = query(collection(db, "tasks"));
      unsubTaskMessages = onSnapshot(allTasksQuery, (snap) => {
        let total = 0;
        snap.forEach((d) => {
          const data = d.data();
          total += Number(data.unreadMessagesBy?.[userId] || 0);
        });
        set({ unreadMessagesCount: total });
      });

      // Документы — улучшенная версия
      const documentsQuery = query(collection(db, "documents"));
      unsubDocuments = onSnapshot(documentsQuery, (snap) => {
        let total = 0;
        snap.forEach((d) => {
          const data = d.data();
          // Зелёный маркер (unreadBy)
          if (Array.isArray(data.unreadBy) && data.unreadBy.includes(userId)) {
            total += 1;
          }
          // Оранжевый маркер (сообщения)
          total += Number(data.unreadMessagesBy?.[userId] || 0);
        });
        set({ unreadDocumentsCount: total });
      });
    },

    incrementDocumentNotifications: (count: number = 1) => {
      set((state) => ({
        unreadDocumentsCount: state.unreadDocumentsCount + count,
      }));
    },

    markTaskAsRead: async (taskId: string) => {
      const { user } = useAuthStore.getState();
      if (!user || !taskId) return;
      const userId = String(user.id || user.uid || "");
      try {
        await updateDoc(doc(db, "tasks", taskId), {
          unreadBy: arrayRemove(userId),
          [`unreadMessagesBy.${userId}`]: 0,
        });
      } catch (err) {
        console.error("Ошибка markTaskAsRead:", err);
      }
    },

    markDocumentAsRead: async (docId: string) => {
      const { user } = useAuthStore.getState();
      if (!user || !docId) return;
      const userId = String(user.id || user.uid || "");
      try {
        await updateDoc(doc(db, "documents", docId), {
          unreadBy: arrayRemove(userId),
          [`unreadMessagesBy.${userId}`]: 0,
        });
      } catch (err) {
        console.error("Ошибка markDocumentAsRead:", err);
      }
    },

    cleanup: () => {
      unsubTasks?.();
      unsubTaskMessages?.();
      unsubDocuments?.();
      set({ 
        unreadTasksCount: 0, 
        unreadMessagesCount: 0, 
        unreadDocumentsCount: 0 
      });
    },

    resetCounters: () => {
      set({ 
        unreadTasksCount: 0, 
        unreadMessagesCount: 0, 
        unreadDocumentsCount: 0 
      });
    },
  };
});